import { FC } from 'react'
import {
  Checkbox,
  ListItemText,
  Menu,
  MenuItem as MuiMenuItem,
  MenuProps,
  styled,
  Typography,
  useTheme,
} from '@mui/material'
import ArrowRightIcon from '@mui/icons-material/ArrowRight'
import { Flex } from 'src/styles/flexComponent'
import { getThemeColor } from 'src/utils'
import { MUIThemeColors } from 'src/mui-theme'
import { CenteredSpinner } from 'src/components/Spinner'
import { EmptyStatusCard } from 'src/components/mooring/EmptyStatusCard'
import NoData from '../../../assets/svg/no-locality-content.svg'

export interface GenericMenuProps extends Omit<MenuProps, 'open'> {
  isOpen: boolean
  onClose: () => void
  menuItems: MenuItemProps[]
  menuWidth?: string
  isLoading?: boolean
}

export interface MenuItemProps {
  id: string | number
  label: string
  checked?: boolean
  hasSubmenu?: boolean
  onClick: (event: React.MouseEvent<HTMLElement>, id: string | number) => void
  selected?: boolean
  height?: string
  width?: string
  extraInfo?: {
    secondary?: string
    description?: string
  }
  onCheckBoxChange?: ((event: React.ChangeEvent<HTMLInputElement>, checked: boolean) => void) | undefined
}

export const GenericMenu: FC<GenericMenuProps> = ({
  anchorEl,
  isOpen,
  onClose,
  menuItems,
  menuWidth = 'auto',
  isLoading,
  ...menuProps
}) => {
  return (
    <StyledMenu anchorEl={anchorEl} open={isOpen} onClose={onClose} isLoading={isLoading} {...menuProps}>
      {isLoading ? <CenteredSpinner size={50} /> : menuItems.length > 0 ? (
        menuItems.map(item => <MenuItem key={item.id} itemData={item} />)
      ) : (
        <EmptyStatusCard imageWidth="80px" image={NoData} headerFontSize="16px" contentFontSize="14px" />
      )}
    </StyledMenu>
  )
}

interface MenuItemComponentProps {
  itemData: MenuItemProps
}

const MenuItem: FC<MenuItemComponentProps> = ({ itemData }) => {
  const {
    id,
    label,
    checked = false,
    hasSubmenu = false,
    onClick,
    selected = false,
    height = '48px',
    width = 'auto',
    extraInfo,
    onCheckBoxChange,
  } = itemData
  const theme = useTheme()

  return (
    <MuiMenuItem
      value={id}
      sx={{
        width: width,
        height: height,
      }}
      onClick={event => onClick(event, id)}
      selected={selected}
    >
      <Checkbox checked={checked} onChange={onCheckBoxChange} />

      {extraInfo ? (
        <Flex.Column>
          <Flex.Row sx={{ justifyContent: 'space-between', alignItems: 'center', width: '100%' }}>
            <ListItemText primary={label} sx={{ paddingRight: '16px' }} />
            {extraInfo.secondary && (
              <Typography variant="body2" color={getThemeColor(theme, MUIThemeColors.secondaryMain)}>
                {extraInfo.secondary}
              </Typography>
            )}
          </Flex.Row>
          {extraInfo.description && (
            <Typography variant="body2" color={getThemeColor(theme, MUIThemeColors.secondaryMain)}>
              {extraInfo.description}
            </Typography>
          )}
        </Flex.Column>
      ) : (
        <ListItemText primary={label} />
      )}

      {hasSubmenu && <ArrowRightIcon sx={{ fontSize: hasSubmenu ? '30px' : '0' }} />}
    </MuiMenuItem>
  )
}

export const StyledMenu = styled(Menu)<{ isLoading?: boolean, maxHeight?: number }>(({ isLoading, maxHeight }) => ({
  '& .MuiList-root': {
    width: 'auto',
    maxHeight: 'auto',
    maxWidth: 'auto',
  },
  '& .MuiPopover-paper': {
    overflow: 'hidden',
    maxWidth: 'auto',
    maxHeight: `${maxHeight ?? 300}px`,
    overflowY: isLoading ? 'hidden' : 'auto',
    ...(isLoading && {
      width: '200px',
      height: '200px',
      display: 'flex',
      justifyContent: 'center',
      alignItems: 'center',
    }),
  },
}))
